'use client'

import { useState, useEffect } from 'react'
import Link from 'next/link'
import { TopBar, BottomNav } from '@/components/layout/navigation'
import { Ticket, Loader2, ArrowLeft } from 'lucide-react'
import { useOrderStore } from '@/store/order-store'

interface Coupon {
  id: string
  name: string
  type: '满减' | '折扣' | '免运费'
  value: number
  minAmount: number
  threshold: number
  startTime: string
  endTime: string
  used: boolean
}

const TABS = [
  { key: 'available', label: '可使用' },
  { key: 'used', label: '已使用' },
  { key: 'expired', label: '已过期' },
] as const

type TabKey = typeof TABS[number]['key']

export default function CouponsPage() {
  const storeCoupons = useOrderStore(s => s.coupons)
  const [activeTab, setActiveTab] = useState<TabKey>('available')
  const [loading, setLoading] = useState(true)
  const [dbCoupons, setDbCoupons] = useState<Coupon[]>([])

  useEffect(() => {
    fetch('/api/coupons')
      .then(res => res.json())
      .then(data => {
        setDbCoupons(data.items || [])
      })
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [])

  // 合并 store 中的优惠券和 DB 中的优惠券，store 优先（含已使用状态）
  const allCoupons = storeCoupons.map(c => ({
    id: c.id,
    name: c.name,
    type: c.type,
    value: c.value,
    minAmount: c.minAmount,
    threshold: c.threshold,
    startTime: c.startTime,
    endTime: c.endTime,
    used: c.used,
  }))

  const now = new Date()
  const today = now.toISOString().split('T')[0]

  const filteredCoupons = allCoupons.filter(c => {
    const isExpired = c.endTime < today
    if (activeTab === 'available') return !c.used && !isExpired
    if (activeTab === 'used') return c.used
    if (activeTab === 'expired') return !c.used && isExpired
    return true
  })

  const availableCount = allCoupons.filter(c => !c.used && c.endTime >= today).length
  const usedCount = allCoupons.filter(c => c.used).length
  const expiredCount = allCoupons.filter(c => !c.used && c.endTime < today).length

  const counts = { available: availableCount, used: usedCount, expired: expiredCount }

  return (
    <div className="min-h-screen pb-16 bg-gray-50">
      <TopBar />

      {/* 头部 */}
      <div className="px-4 pt-2 pb-3 bg-gradient-to-r from-pink-500 to-rose-400">
        <div className="flex items-center gap-2 mb-3">
          <Link href="/profile" className="p-1"><ArrowLeft className="w-5 h-5 text-white" /></Link>
          <h1 className="text-base font-bold text-white">我的优惠券</h1>
        </div>
        <div className="flex items-center gap-6">
          <div className="text-center">
            <p className="text-2xl font-bold text-white">{availableCount}</p>
            <p className="text-[10px] text-white/70">可使用</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold text-white/60">{usedCount}</p>
            <p className="text-[10px] text-white/70">已使用</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold text-white/60">{expiredCount}</p>
            <p className="text-[10px] text-white/70">已过期</p>
          </div>
        </div>
      </div>

      {/* Tab */}
      <div className="flex bg-white border-b border-gray-100 sticky top-12 z-20">
        {TABS.map(tab => (
          <button key={tab.key} onClick={() => setActiveTab(tab.key)}
            className={`flex-1 py-3 text-xs font-medium relative transition-colors ${activeTab === tab.key ? 'text-pink-500' : 'text-gray-400'}`}>
            {tab.label}
            {counts[tab.key] > 0 && (
              <span className={`ml-0.5 text-[10px] ${activeTab === tab.key ? 'text-pink-500' : 'text-gray-300'}`}>({counts[tab.key]})</span>
            )}
            {activeTab === tab.key && <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-8 h-0.5 bg-pink-500 rounded-full" />}
          </button>
        ))}
      </div>

      {/* 优惠券列表 */}
      <div className="px-4 py-3 space-y-3">
        {filteredCoupons.length > 0 ? filteredCoupons.map(coupon => {
          const isExpired = coupon.endTime < today
          const isDisabled = coupon.used || isExpired
          return (
            <div key={coupon.id} className={`relative bg-white rounded-xl overflow-hidden ${isDisabled ? 'opacity-50' : ''}`}>
              {/* 左侧锯齿效果 */}
              <div className="flex">
                {/* 金额区域 */}
                <div className={`w-24 flex flex-col items-center justify-center py-4 flex-shrink-0 ${isDisabled ? 'bg-gray-200' : 'bg-gradient-to-br from-pink-500 to-rose-400'}`}>
                  {coupon.type === '满减' && (
                    <>
                      <span className="text-xs text-white/80">¥</span>
                      <span className="text-2xl font-bold text-white">{coupon.value}</span>
                      <span className="text-[9px] text-white/70">满{coupon.minAmount}可用</span>
                    </>
                  )}
                  {coupon.type === '折扣' && (
                    <>
                      <span className="text-2xl font-bold text-white">{coupon.value / 10}</span>
                      <span className="text-[9px] text-white/70">折</span>
                    </>
                  )}
                  {coupon.type === '免运费' && (
                    <>
                      <span className="text-sm font-bold text-white">免运费</span>
                      <span className="text-[9px] text-white/70">无门槛</span>
                    </>
                  )}
                </div>

                {/* 信息区域 */}
                <div className="flex-1 py-3 px-3">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-bold text-gray-800">{coupon.name}</p>
                    {coupon.used && (
                      <span className="px-1.5 py-0.5 bg-gray-100 text-gray-400 text-[9px] rounded">已使用</span>
                    )}
                    {isExpired && (
                      <span className="px-1.5 py-0.5 bg-gray-100 text-gray-400 text-[9px] rounded">已过期</span>
                    )}
                  </div>
                  <p className="text-[10px] text-gray-400 mt-1">
                    {coupon.type === '满减' ? `满${coupon.minAmount}元减${coupon.value}元` : coupon.type === '折扣' ? `全场${coupon.value / 10}折` : '全场免运费'}
                  </p>
                  <p className="text-[10px] text-gray-300 mt-1.5">
                    {coupon.startTime} ~ {coupon.endTime}
                  </p>
                </div>
              </div>

              {/* 已使用遮罩 */}
              {coupon.used && (
                <div className="absolute top-2 right-10 w-12 h-12 border-2 border-gray-300 rounded-full flex items-center justify-center">
                  <span className="text-gray-400 text-lg font-bold" style={{ transform: 'rotate(-30deg)' }}>已用</span>
                </div>
              )}
            </div>
          )
        }) : (
          <div className="text-center py-16">
            <Ticket className="w-12 h-12 text-gray-200 mx-auto mb-3" />
            <p className="text-sm text-gray-400">
              {activeTab === 'available' ? '暂无可用优惠券' : activeTab === 'used' ? '暂无已使用的优惠券' : '暂无已过期的优惠券'}
            </p>
          </div>
        )}
      </div>

      {/* 底部去使用按钮 */}
      {!loading && filteredCoupons.length > 0 && activeTab === 'available' && (
        <div className="fixed bottom-16 left-0 right-0 bg-white border-t border-gray-100 px-4 py-3 z-10">
          <Link href="/menu" className="block w-full py-2.5 bg-pink-500 text-white text-sm font-bold rounded-xl text-center active:bg-pink-600">
            去使用优惠券
          </Link>
        </div>
      )}

      <BottomNav />
    </div>
  )
}
